import { FILE_MIME_TYPES } from "@miplatform/constants";
import {
  IApiToastMessageConfig,
  IDocumentPayload,
  IDocumentPayloadParams,
} from "@miplatform/types";
import {
  API_VERSION,
  DOCUMENT_SERVICE,
  X_API_VERSION,
} from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_DOCUMENTS = `${DOCUMENT_SERVICE}/documents`;

export const documents = {
  getDocumentOptions: ({
    data,
    params,
    contentType,
  }: {
    data: File;
    params: IDocumentPayloadParams;
    contentType?: string;
  }) => {
    return apiOptions({
      url: `${API_DOCUMENTS}`,
      method: "post",
      headers: {
        "Content-Type": contentType || "image/jpeg",
      },
      params,
      data,
    });
  },

  getDocumentOptionsFormData: ({
    uri,
    imageAnnotation = null,
    captionTop = null,
    captionBottom = null,
    params,
    contentType = null,
  }: {
    uri: string;
    imageAnnotation?: string | null;
    captionTop?: string | null;
    captionBottom?: string | null;
    params: IDocumentPayloadParams;
    contentType?: string | null;
  }) => {
    const data = new FormData();

    data.append("document", {
      uri,
      name: params.title || "noTitleProvided",
      type: contentType || FILE_MIME_TYPES.PDF,
    } as unknown as Blob);
    if (params?.externalReferenceId) {
      data.append("externalReferenceId", params.externalReferenceId);
    }
    if (imageAnnotation) {
      data.append("annotation", {
        uri: imageAnnotation,
        name: "annotation.jpeg",
        type: FILE_MIME_TYPES.JPEG,
      } as unknown as Blob);
    }

    if (captionTop || captionBottom) {
      data.append(
        "ExtraInfo",
        JSON.stringify({ CaptionTop: captionTop, CaptionBottom: captionBottom })
      );
    }

    if (params?.documentId) {
      data.append("documentId", params.documentId);
    }

    if (params?.documentType) {
      data.append("documentType", params.documentType);
    }

    return apiOptions({
      url: `${API_DOCUMENTS}/multipart`,
      method: "post",
      params,
      headers: {
        [X_API_VERSION]: API_VERSION,
        "Content-Type": "multipart/form-data",
      },
      data,
    });
  },
  create: async ({
    data,
    params,
    contentType,
    toastMessageConfig,
  }: {
    data: File;
    params: IDocumentPayloadParams;
    contentType?: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await documents.getDocumentOptions({
      data,
      params,
      contentType,
    });
    return apiRequest(options, toastMessageConfig);
  },

  submitDocumentWithFormData: async ({
    uri,
    params,
  }: {
    uri: string;
    params: IDocumentPayloadParams;
  }) => {
    const options = await documents.getDocumentOptionsFormData({
      uri,
      params,
    });

    return apiRequest(options);
  },
  getById: async ({ id }: { id: string }) => {
    const options = await apiOptions({
      url: `${API_DOCUMENTS}/${id}`,
      method: "get",
    });
    return apiRequest(options);
  },
  getDeleteDocumentOptions: async ({
    id,
    modifiedOn,
  }: {
    id: string;
    modifiedOn: string;
  }) =>
    apiOptions({
      url: `${API_DOCUMENTS}/${id}`,
      method: "delete",
      data: { modifiedOn },
    }),
  delete: async ({
    id,
    modifiedOn,
    toastMessageConfig,
  }: {
    id: string;
    modifiedOn: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await documents.getDeleteDocumentOptions({
      id,
      modifiedOn,
    });

    return apiRequest(options, toastMessageConfig);
  },
  uploadWatermark: async ({
    data,
    params,
    contentType = "image/png",
    toastMessageConfig,
  }: {
    data: IDocumentPayload;
    params: IDocumentPayloadParams;
    contentType?: string;
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await apiOptions({
      url: `${DOCUMENT_SERVICE}/watermark`,
      method: "post",
      headers: {
        "Content-Type": contentType,
      },
      params,
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  getVideoChunkOptions: async ({
    documentId,
    chunk,
    blockId,
  }: {
    documentId: string;
    chunk: unknown;
    blockId: string;
  }) => {
    return apiOptions({
      url: `${API_DOCUMENTS}/${documentId}/upload-block/${blockId}/multipart`,
      method: "post",
      data: chunk,
      headers: {
        [X_API_VERSION]: API_VERSION,
        "Content-Type": "multipart/form-data",
      },
    });
  },
  uploadVideoChunk: async ({
    params,
    toastMessageConfig,
  }: {
    params: { documentId: string; chunk: unknown; blockId: string };
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await documents.getVideoChunkOptions({ ...params });
    return apiRequest(options, toastMessageConfig);
  },
  getVideoFinalizeOptions: async ({
    documentId,
    blockIdsLength,
    data,
  }: {
    documentId: string;
    blockIdsLength: number;
    data: unknown;
  }) => {
    return apiOptions({
      url: `${API_DOCUMENTS}/${documentId}/commit-blocks/${blockIdsLength}`,
      method: "post",
      data,
      headers: {
        [X_API_VERSION]: API_VERSION,
      },
    });
  },
  finalizeVideoUpload: async ({
    params,
    toastMessageConfig,
  }: {
    params: { documentId: string; blockIdsLength: number; data: unknown };
    toastMessageConfig?: IApiToastMessageConfig;
  }) => {
    const options = await documents.getVideoFinalizeOptions({ ...params });
    return apiRequest(options, toastMessageConfig);
  },
};
